"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import { ChevronDown } from "@/lib/icons"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

interface SourceFilterBarProps {
  groups: { id: string; label: string; content: ReactNode }[]
}

// One container for every active source's own filter controls.
//
// Each source used to render its strip as an independent sibling, so enabling
// several at once stacked them: several different "All" pills down the page with
// nothing saying which belonged to which source, and inconsistent styling between
// them. Stacking also grew the bar without bound — with four sources active it ate
// enough vertical space to push content off screen.
//
// So with more than one source the bar collapses to a single row and a source
// picker. That keeps it one row tall no matter how many sources are on, which is
// what makes it viable to stick it under the header: the filters stay reachable
// after scrolling into a long result set, instead of forcing a scroll back to the
// top to change a category.
export function SourceFilterBar({ groups }: SourceFilterBarProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  // Translate a vertical wheel/trackpad gesture into horizontal scrolling.
  // A container that only overflows on X doesn't respond to a normal wheel —
  // without this the pills past the edge are reachable only by dragging or
  // shift+wheel, which nobody discovers. Touch needs nothing here: native
  // overflow-x panning already handles a finger drag (and handles it better,
  // with real momentum), which is why this deliberately doesn't hijack
  // pointer/touch events.
  //
  // Attached manually rather than via onWheel because React's synthetic wheel
  // listener is passive — preventDefault() there is a no-op and the page would
  // scroll behind the bar at the same time.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return // nothing to scroll
      // Trackpads send real horizontal deltas; a mouse wheel only sends Y.
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
      if (delta === 0) return

      // Hand the gesture back to the page once this row is at its end, so the
      // bar scrolls the strip but never becomes a scroll trap the page can't
      // escape.
      const atStart = el.scrollLeft <= 0
      const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth
      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return

      e.preventDefault()
      el.scrollLeft += delta
    }

    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  }, [])

  // Fall back to the first group whenever the selected source goes away (user
  // deselected it) — otherwise the bar would render empty with a stale label.
  useEffect(() => {
    if (groups.length === 0) return
    if (!activeId || !groups.some((g) => g.id === activeId)) {
      setActiveId(groups[0].id)
    }
  }, [groups, activeId])

  if (groups.length === 0) return null

  const active = groups.find((g) => g.id === activeId) ?? groups[0]
  const multiple = groups.length > 1

  return (
    <div
      className={cn(
        "sticky z-20 w-full border-b border-border",
        // `bg-card` is deliberately translucent in this theme (alpha .6 light / .5
        // dark), so as a floating rounded card over scrolling posts this read as a
        // see-through overlay rather than part of the app. Full-bleed + opaque
        // `bg-background` + a bottom border makes it a continuation of the header
        // chrome it's pinned to, which is what it actually is.
        "bg-background",
      )}
      // Flush against the app header, whose height is measured and published by
      // app/page.tsx (it varies with the linking banner / tag-set row / wrapping).
      style={{ top: "var(--browse-header-h, 0px)" }}
    >
      <div className="flex items-center gap-2 px-3 py-1.5">
        {multiple ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="shrink-0 flex items-center gap-1 rounded-md border border-border/60 bg-background/60 px-2 py-1 text-[11px] font-medium text-foreground hover:bg-background transition-colors">
                <span className="max-w-[7rem] truncate">{active.label}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[9rem]">
              {groups.map((g) => (
                <DropdownMenuItem
                  key={g.id}
                  onClick={() => setActiveId(g.id)}
                  className={cn("text-xs", g.id === active.id && "font-semibold")}
                >
                  {g.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          // Single source: the attribution is unambiguous, so the label would just
          // be noise — but keep the width so the pills don't jump when a second
          // source is toggled on.
          <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
            {active.label}
          </span>
        )}

        {/* `w-fit mx-auto` rather than `justify-center` on the row itself: when a
            source has more pills than fit, justify-center leaves the overflow to
            the left of the scroll origin and the first pills become unreachable.
            This centers while short, and collapses to a normal left-origin scroll
            once the content is wider than the track.

            This is the ONLY scroll container in the bar. Each strip used to set
            its own `overflow-x-auto` as well, nested inside this one, which
            rendered two stacked horizontal scrollbars whenever the pills
            overflowed. `scrollbar-hide` then drops the visible track entirely —
            it's a one-row control strip, and a scrollbar under it read as a
            broken frame rather than an affordance; the row still scrolls by
            wheel, trackpad, drag and keyboard. */}
        <div ref={scrollerRef} className="min-w-0 flex-1 overflow-x-auto scrollbar-hide">
          <div className="w-fit mx-auto">{active.content}</div>
        </div>
      </div>
    </div>
  )
}
